// src/cockpit4/components/LeadMiner.tsx
// ============================================
// P1-功能4：智能线索挖掘（客户中心）
//
// 功能：
//   1. 基于现有客户画像找相似客户（分析现有客户行业/规模/地域分布）
//   2. 行业/地域/规模智能推荐（内置行业关键词库，推荐高意向行业）
//   3. 线索质量自动评分（0-100，基于行业匹配度/规模/地域等）
//   4. 一键导入和分配（推荐线索导入 CRM，调用 RuntimeManager.execute(CRM, 'create', {profile})）
//   5. 线索来源追踪（行业推荐/相似客户/手动录入）
//   6. 线索转化漏斗（统计线索→客户转化率）
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" }) 现有客户用于分析画像
//   - localStorage（key: xeaos_leads）持久化线索列表
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  Plus,
  Radar,
  Target,
  TrendingUp,
  MapPin,
  Building2,
  CheckCircle2,
  X,
  Filter,
  Download,
  Users,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { CRMCommand } from "../../runtime/CRMRuntime/types";
import type { CustomerProfile, CustomerCategory } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型与常量
// ============================================

const LEADS_STORAGE_KEY = "xeaos_leads";

/** 线索来源 */
type LeadSource = "industry" | "similar" | "manual";

/** 线索状态 */
type LeadStatus = "new" | "imported" | "rejected";

/** 挖掘出的线索 */
interface MinedLead {
  id: string;
  company: string;
  industry: string;
  region: string;
  scale: "small" | "medium" | "large";
  source: LeadSource;
  score: number;
  status: LeadStatus;
  createdAt: string;
  importedCustomerId?: string;
  reason: string;
  contactName: string;
  contactPhone: string;
}

/** 行业推荐关键词库（高意向行业画像） */
interface IndustryProfile {
  key: string;
  label: string;
  /** 推荐地域 */
  regions: string[];
  /** 推荐规模 */
  scale: "small" | "medium" | "large";
  /** 行业热度权重 0-100 */
  heat: number;
  color: string;
}

/** 内置行业关键词库（用于智能推荐，非客户数据） */
const INDUSTRY_LIBRARY: IndustryProfile[] = [
  { key: "hotel", label: "酒店/住宿", regions: ["华东", "华南", "西南"], scale: "medium", heat: 88, color: "#FBBF24" },
  { key: "ai", label: "AI/智能硬件", regions: ["北京", "深圳", "杭州"], scale: "medium", heat: 92, color: "#38BDF8" },
  { key: "saas", label: "SaaS/企业服务", regions: ["北京", "上海", "深圳"], scale: "medium", heat: 85, color: "#A78BFA" },
  { key: "manufacture", label: "智能制造", regions: ["长三角", "珠三角"], scale: "large", heat: 78, color: "#34D399" },
  { key: "retail", label: "零售连锁", regions: ["华东", "华南"], scale: "large", heat: 72, color: "#FB923C" },
  { key: "education", label: "教育培训", regions: ["全国"], scale: "medium", heat: 65, color: "#60A5FA" },
];

const SCALE_LABEL: Record<MinedLead["scale"], string> = {
  small: "小型",
  medium: "中型",
  large: "大型",
};

const SOURCE_LABEL: Record<LeadSource, string> = {
  industry: "行业推荐",
  similar: "相似客户",
  manual: "手动录入",
};

const SOURCE_COLOR: Record<LeadSource, string> = {
  industry: "#38BDF8",
  similar: "#A78BFA",
  manual: "#9CA3AF",
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 从现有客户提取 Top N 行业分布 */
function extractTopIndustries(customers: CustomerProfile[], topN = 5): { industry: string; count: number }[] {
  const counter = new Map<string, number>();
  for (const c of customers) {
    const ind = (c.industry || "").trim();
    if (!ind) continue;
    counter.set(ind, (counter.get(ind) || 0) + 1);
  }
  return Array.from(counter.entries())
    .map(([industry, count]) => ({ industry, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/** 从现有客户提取 Top N 地域分布 */
function extractTopRegions(customers: CustomerProfile[], topN = 5): { region: string; count: number }[] {
  const counter = new Map<string, number>();
  for (const c of customers) {
    const r = (c.region || "").trim();
    if (!r) continue;
    counter.set(r, (counter.get(r) || 0) + 1);
  }
  return Array.from(counter.entries())
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/**
 * 线索质量自动评分（0-100）
 * 维度：
 *   - 行业匹配度（与现有客户行业一致 +30，与行业库匹配 +20）
 *   - 地域匹配度（与现有客户 Top 地域一致 +20）
 *   - 规模匹配度（与行业库推荐规模一致 +15）
 *   - 行业热度（行业库 heat 归一化 +15）
 */
function scoreLead(
  lead: { industry: string; region: string; scale: MinedLead["scale"] },
  ctx: { topIndustries: string[]; topRegions: string[] }
): number {
  let score = 30;
  // 行业匹配度
  if (ctx.topIndustries.includes(lead.industry)) {
    score += 30;
  } else {
    const ind = INDUSTRY_LIBRARY.find((p) => p.key === lead.industry || p.label === lead.industry);
    if (ind) score += 20;
  }
  // 地域匹配度
  if (ctx.topRegions.includes(lead.region)) {
    score += 20;
  } else {
    const ind = INDUSTRY_LIBRARY.find((p) => p.regions.includes(lead.region));
    if (ind) score += 10;
  }
  // 规模匹配度
  const indProfile = INDUSTRY_LIBRARY.find((p) => p.key === lead.industry || p.label === lead.industry);
  if (indProfile && indProfile.scale === lead.scale) {
    score += 15;
  }
  // 行业热度
  if (indProfile) {
    score += Math.round(indProfile.heat * 0.15);
  }
  return Math.max(0, Math.min(100, score));
}

/** 意向分级 */
function intentLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "高质量", color: "#34D399" };
  if (score >= 60) return { label: "中质量", color: "#FBBF24" };
  if (score >= 40) return { label: "低质量", color: "#FB923C" };
  return { label: "待评估", color: "#6B7280" };
}

/** 安全读取 localStorage */
function loadLeads(): MinedLead[] {
  try {
    const raw = localStorage.getItem(LEADS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MinedLead[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage */
function saveLeads(leads: MinedLead[]): void {
  try {
    localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(leads));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

// ============================================
// 主组件
// ============================================

export default function LeadMiner() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [leads, setLeads] = useState<MinedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [filterSource, setFilterSource] = useState<LeadSource | "all">("all");
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<Record<string, { ok: boolean; text: string }>>({});

  // 手动录入表单
  const [manualForm, setManualForm] = useState({
    company: "",
    industry: "",
    region: "",
    scale: "medium" as MinedLead["scale"],
    contactName: "",
    contactPhone: "",
  });

  // 加载真实客户列表（用于画像分析）
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载：加载客户 + 加载本地线索
  useEffect(() => {
    setLeads(loadLeads());
    void loadCustomers();
  }, [loadCustomers]);

  // 线索变化时持久化
  useEffect(() => {
    saveLeads(leads);
  }, [leads]);

  // ============================================
  // 派生数据：画像分析
  // ============================================
  const portrait = useMemo(() => {
    const topIndustries = extractTopIndustries(customers).map((x) => x.industry);
    const topRegions = extractTopRegions(customers).map((x) => x.region);
    const total = customers.length;
    return { topIndustries, topRegions, total };
  }, [customers]);

  /** 基于现有客户行业分布 + 行业库，生成行业推荐线索 */
  const generateIndustryLeads = useCallback(() => {
    if (customers.length === 0) return;
    const ctx = {
      topIndustries: portrait.topIndustries,
      topRegions: portrait.topRegions,
    };
    // 1. 优先基于现有客户行业分布找相似线索
    const similar: MinedLead[] = [];
    for (const ind of portrait.topIndustries.slice(0, 3)) {
      const region = portrait.topRegions[0] || "华东";
      const lead: MinedLead = {
        id: genId("lead_similar"),
        company: `${ind}行业潜在客户`,
        industry: ind,
        region,
        scale: "medium",
        source: "similar",
        score: 0,
        status: "new",
        createdAt: todayStr(),
        reason: `与现有 ${portrait.total} 家客户中的 ${ind} 行业客户画像匹配`,
        contactName: "",
        contactPhone: "",
      };
      lead.score = scoreLead(lead, ctx);
      similar.push(lead);
    }
    // 2. 基于行业库推荐高意向行业（去重：行业库中尚未在现有客户中出现的）
    const existingIndustries = new Set(portrait.topIndustries);
    const industryLeads: MinedLead[] = INDUSTRY_LIBRARY.filter(
      (p) => !existingIndustries.has(p.key) && !existingIndustries.has(p.label)
    )
      .slice(0, 3)
      .map((p) => {
        const lead: MinedLead = {
          id: genId("lead_industry"),
          company: `${p.label}领域潜在客户`,
          industry: p.label,
          region: p.regions[0] || "全国",
          scale: p.scale,
          source: "industry",
          score: 0,
          status: "new",
          createdAt: todayStr(),
          reason: `行业库推荐：${p.label}热度 ${p.heat}，推荐地域 ${p.regions.join("/")}`,
          contactName: "",
          contactPhone: "",
        };
        lead.score = scoreLead(lead, ctx);
        return lead;
      });
    setLeads((prev) => [...industryLeads, ...similar, ...prev]);
  }, [customers, portrait]);

  /** 手动录入线索 */
  const submitManualLead = useCallback(() => {
    if (!manualForm.company.trim()) return;
    const ctx = {
      topIndustries: portrait.topIndustries,
      topRegions: portrait.topRegions,
    };
    const lead: MinedLead = {
      id: genId("lead_manual"),
      company: manualForm.company.trim(),
      industry: manualForm.industry.trim() || "其他",
      region: manualForm.region.trim() || "全国",
      scale: manualForm.scale,
      source: "manual",
      score: 0,
      status: "new",
      createdAt: todayStr(),
      reason: "手动录入线索",
      contactName: manualForm.contactName.trim(),
      contactPhone: manualForm.contactPhone.trim(),
    };
    lead.score = scoreLead(lead, ctx);
    setLeads((prev) => [lead, ...prev]);
    setManualForm({ company: "", industry: "", region: "", scale: "medium", contactName: "", contactPhone: "" });
    setShowManualForm(false);
  }, [manualForm, portrait]);

  /** 一键导入线索到 CRM（通过 RuntimeManager.execute(CRM, 'create', {profile})） */
  const importLead = useCallback(
    async (lead: MinedLead) => {
      setImportingId(lead.id);
      setImportMsg((s) => ({ ...s, [lead.id]: { ok: true, text: "导入中..." } }));
      try {
        if (!RuntimeManager.isInitialized()) {
          throw new Error("RuntimeManager 尚未初始化");
        }
        // 构造客户档案（与 CRMService.createCustomer 期望的 CustomerProfile 一致）
        const customerId = `${lead.company.replace(/[\\/:*?"<>|]/g, "_")}_${Date.now().toString(36)}`;
        const profile: CustomerProfile = {
          id: customerId,
          company: lead.company,
          industry: lead.industry,
          region: lead.region,
          source: SOURCE_LABEL[lead.source],
          tags: [`线索挖掘`, SOURCE_LABEL[lead.source], `质量${lead.score}`],
          tier: lead.score >= 80 ? "S" : lead.score >= 60 ? "A" : lead.score >= 40 ? "B" : "C",
          category: "potential" as CustomerCategory,
          owner: "待分配",
          contact: {
            name: lead.contactName || "",
            phone: lead.contactPhone || "",
            wechat: "",
            email: "",
          },
          followup: {
            lastContact: "",
            nextContact: todayStr(),
            status: "prospecting",
            priority: lead.score >= 70 ? "high" : lead.score >= 50 ? "medium" : "low",
            estimatedValue: 0,
          },
          history: `- ${todayStr()}: ${lead.reason}（来源：${SOURCE_LABEL[lead.source]}，质量分 ${lead.score}）`,
          deals: "",
          analysis: "",
          suggestion: `基于画像分析推荐，质量分 ${lead.score}，建议尽快建联。`,
          filePath: `XEAOS/CRM/${customerId}.md`,
        };
        const resp = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.Create, { profile });
        if (resp?.success !== true) {
          throw new Error(resp?.error || "创建客户档案失败");
        }
        // 更新线索状态
        setLeads((prev) =>
          prev.map((l) =>
            l.id === lead.id ? { ...l, status: "imported" as LeadStatus, importedCustomerId: customerId } : l
          )
        );
        setImportMsg((s) => ({
          ...s,
          [lead.id]: { ok: true, text: `已导入 CRM，客户 ID：${customerId}` },
        }));
      } catch (err) {
        setImportMsg((s) => ({
          ...s,
          [lead.id]: { ok: false, text: `导入失败：${(err as Error).message || String(err)}` },
        }));
      } finally {
        setImportingId(null);
      }
    },
    []
  );

  /** 标记线索为已拒绝 */
  const rejectLead = useCallback((id: string) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: "rejected" as LeadStatus } : l)));
  }, []);

  /** 删除线索 */
  const removeLead = useCallback((id: string) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
  }, []);

  // ============================================
  // 派生：转化漏斗统计
  // ============================================
  const funnel = useMemo(() => {
    const total = leads.length;
    const imported = leads.filter((l) => l.status === "imported").length;
    const rejected = leads.filter((l) => l.status === "rejected").length;
    const pending = leads.filter((l) => l.status === "new").length;
    const conversionRate = total > 0 ? Math.round((imported / total) * 100) : 0;
    const avgScore = total > 0 ? Math.round(leads.reduce((s, l) => s + l.score, 0) / total) : 0;
    return { total, imported, rejected, pending, conversionRate, avgScore };
  }, [leads]);

  // 过滤后的线索列表
  const filteredLeads = useMemo(() => {
    const list = filterSource === "all" ? leads : leads.filter((l) => l.source === filterSource);
    return [...list].sort((a, b) => b.score - a.score);
  }, [leads, filterSource]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-violet-500/15 border border-violet-500/30">
            <Radar size={13} className="text-violet-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能线索挖掘</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Lead Miner · {funnel.total} 线索 · 转化 {funnel.conversionRate}% · 均分 {funnel.avgScore}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowManualForm((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
          >
            <Plus size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">手动录入</span>
          </button>
          <button
            onClick={loadCustomers}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 hover:bg-violet-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 size={11} className="text-violet-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-violet-400" />
            )}
            <span className="text-[9px] font-mono text-violet-300 uppercase tracking-wider">
              {refreshing ? "刷新中..." : "刷新画像"}
            </span>
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-violet-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载客户画像...</p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI 概览 */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={Target} label="线索总数" value={String(funnel.total)} color="#A78BFA" />
            <KpiCard icon={Download} label="已导入" value={String(funnel.imported)} color="#34D399" />
            <KpiCard icon={TrendingUp} label="转化率" value={`${funnel.conversionRate}%`} color="#FBBF24" />
            <KpiCard icon={Sparkles} label="平均质量分" value={String(funnel.avgScore)} color="#38BDF8" />
          </div>

          {/* 客户画像分析 + 行业推荐按钮 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Building2 size={11} className="text-violet-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  现有客户画像分析（基于 {portrait.total} 家客户）
                </span>
              </div>
              <button
                onClick={generateIndustryLeads}
                disabled={customers.length === 0}
                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles size={10} />
                智能挖掘线索
              </button>
            </div>
            {customers.length === 0 ? (
              <p className="text-[11px] font-mono text-gray-600 text-center py-3">暂无客户数据，无法分析画像</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* Top 行业 */}
                <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                  <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">Top 行业分布</div>
                  <div className="space-y-1">
                    {portrait.topIndustries.length === 0 ? (
                      <p className="text-[10px] font-mono text-gray-600">无行业数据</p>
                    ) : (
                      portrait.topIndustries.slice(0, 5).map((ind) => {
                        const count = customers.filter((c) => c.industry === ind).length;
                        const maxCount = Math.max(
                          ...portrait.topIndustries.map((i) => customers.filter((c) => c.industry === i).length),
                          1
                        );
                        const widthPct = Math.max(10, Math.round((count / maxCount) * 100));
                        return (
                          <div key={ind} className="flex items-center gap-2">
                            <span className="w-16 text-[10px] font-mono text-gray-300 truncate">{ind}</span>
                            <div className="flex-1 h-3 rounded bg-white/[0.02] overflow-hidden">
                              <div
                                className="h-full rounded"
                                style={{ width: `${widthPct}%`, background: "linear-gradient(90deg, #A78BFA80, #A78BFA)" }}
                              />
                            </div>
                            <span className="text-[9px] font-mono text-gray-400 w-5 text-right">{count}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                {/* Top 地域 */}
                <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
                  <div className="text-[9px] font-mono text-gray-500 uppercase mb-1.5">Top 地域分布</div>
                  <div className="space-y-1">
                    {portrait.topRegions.length === 0 ? (
                      <p className="text-[10px] font-mono text-gray-600">无地域数据</p>
                    ) : (
                      portrait.topRegions.slice(0, 5).map((reg) => {
                        const count = customers.filter((c) => c.region === reg).length;
                        const maxCount = Math.max(
                          ...portrait.topRegions.map((r) => customers.filter((c) => c.region === r).length),
                          1
                        );
                        const widthPct = Math.max(10, Math.round((count / maxCount) * 100));
                        return (
                          <div key={reg} className="flex items-center gap-2">
                            <span className="w-16 text-[10px] font-mono text-gray-300 truncate">{reg}</span>
                            <div className="flex-1 h-3 rounded bg-white/[0.02] overflow-hidden">
                              <div
                                className="h-full rounded"
                                style={{ width: `${widthPct}%`, background: "linear-gradient(90deg, #38BDF880, #38BDF8)" }}
                              />
                            </div>
                            <span className="text-[9px] font-mono text-gray-400 w-5 text-right">{count}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 手动录入表单 */}
          <AnimatePresence>
            {showManualForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-b border-white/[0.04]"
              >
                <div className="p-4 space-y-2 bg-white/[0.01]">
                  <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider mb-1">
                    手动录入线索
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={manualForm.company}
                      onChange={(e) => setManualForm((s) => ({ ...s, company: e.target.value }))}
                      placeholder="公司名称 *"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    />
                    <input
                      value={manualForm.industry}
                      onChange={(e) => setManualForm((s) => ({ ...s, industry: e.target.value }))}
                      placeholder="行业（如 酒店/AI/SaaS）"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    />
                    <input
                      value={manualForm.region}
                      onChange={(e) => setManualForm((s) => ({ ...s, region: e.target.value }))}
                      placeholder="地域（如 华东/北京）"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    />
                    <select
                      value={manualForm.scale}
                      onChange={(e) => setManualForm((s) => ({ ...s, scale: e.target.value as MinedLead["scale"] }))}
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    >
                      <option value="small">小型</option>
                      <option value="medium">中型</option>
                      <option value="large">大型</option>
                    </select>
                    <input
                      value={manualForm.contactName}
                      onChange={(e) => setManualForm((s) => ({ ...s, contactName: e.target.value }))}
                      placeholder="联系人姓名"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    />
                    <input
                      value={manualForm.contactPhone}
                      onChange={(e) => setManualForm((s) => ({ ...s, contactPhone: e.target.value }))}
                      placeholder="联系电话"
                      className="px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setShowManualForm(false)}
                      className="px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200"
                    >
                      取消
                    </button>
                    <button
                      onClick={submitManualLead}
                      disabled={!manualForm.company.trim()}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus size={10} />
                      添加线索
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 来源过滤 */}
          <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04]">
            <Filter size={10} className="text-gray-500" />
            <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">来源：</span>
            {(
              [
                { id: "all", label: "全部" },
                { id: "industry", label: "行业推荐" },
                { id: "similar", label: "相似客户" },
                { id: "manual", label: "手动录入" },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterSource(f.id)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                  filterSource === f.id
                    ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
                    : "text-gray-500 hover:text-gray-300 border border-transparent"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* 线索列表 */}
          <div className="p-4">
            {filteredLeads.length === 0 ? (
              <div className="py-8 text-center">
                <Users size={22} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">
                  {leads.length === 0 ? "暂无线索，点击「智能挖掘线索」开始" : "当前筛选条件下无线索"}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredLeads.map((lead) => {
                  const intent = intentLevel(lead.score);
                  const isImporting = importingId === lead.id;
                  const msg = importMsg[lead.id];
                  return (
                    <div
                      key={lead.id}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5"
                    >
                      <div className="flex items-center gap-2.5">
                        {/* 质量分 */}
                        <div className="flex-shrink-0 w-12 text-center">
                          <div className="text-[16px] font-mono font-bold" style={{ color: intent.color }}>
                            {lead.score}
                          </div>
                          <div className="text-[8px] font-mono uppercase" style={{ color: intent.color }}>
                            {intent.label}
                          </div>
                        </div>
                        {/* 主体信息 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] text-gray-200 truncate">{lead.company}</span>
                            {lead.status === "imported" && (
                              <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                            )}
                            {lead.status === "rejected" && (
                              <X size={11} className="text-red-400 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span
                              className="px-1 rounded text-[8px] font-mono"
                              style={{ background: `${SOURCE_COLOR[lead.source]}20`, color: SOURCE_COLOR[lead.source] }}
                            >
                              {SOURCE_LABEL[lead.source]}
                            </span>
                            <span className="text-[9px] font-mono text-gray-500">{lead.industry}</span>
                            <span className="text-[9px] font-mono text-gray-600">·</span>
                            <span className="flex items-center gap-0.5 text-[9px] font-mono text-gray-500">
                              <MapPin size={8} /> {lead.region}
                            </span>
                            <span className="text-[9px] font-mono text-gray-600">·</span>
                            <span className="text-[9px] font-mono text-gray-500">{SCALE_LABEL[lead.scale]}</span>
                          </div>
                          <div className="text-[9px] font-mono text-gray-600 mt-1 leading-snug">
                            {lead.reason}
                          </div>
                        </div>
                        {/* 操作按钮 */}
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          {lead.status === "new" ? (
                            <>
                              <button
                                onClick={() => void importLead(lead)}
                                disabled={isImporting}
                                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {isImporting ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                                导入
                              </button>
                              <button
                                onClick={() => rejectLead(lead.id)}
                                className="px-2 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400 transition-colors"
                              >
                                忽略
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => removeLead(lead.id)}
                              className="px-2 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400 transition-colors"
                            >
                              删除
                            </button>
                          )}
                        </div>
                      </div>
                      {/* 导入反馈 */}
                      {msg && (
                        <div
                          className={`mt-1.5 px-2 py-1 rounded text-[9px] font-mono border ${
                            msg.ok
                              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                              : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                          }`}
                        >
                          {msg.text}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 转化漏斗 */}
          {leads.length > 0 && (
            <div className="p-4 border-t border-white/[0.04]">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={11} className="text-emerald-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  线索转化漏斗
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <FunnelStep label="挖掘总数" value={funnel.total} color="#A78BFA" />
                <FunnelStep label="待处理" value={funnel.pending} color="#FBBF24" />
                <FunnelStep label="已导入" value={funnel.imported} color="#34D399" />
                <FunnelStep label="已忽略" value={funnel.rejected} color="#6B7280" />
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof Target;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 漏斗步骤小卡片
// ============================================
function FunnelStep({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      className="rounded-md border border-white/[0.04] bg-white/[0.02] px-2 py-1.5 text-center"
      style={{ borderTop: `2px solid ${color}` }}
    >
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-[14px] font-mono font-bold mt-0.5" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
